import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

interface OptionListEditorProps {
  title: string;
  description: string;
  placeholder: string;
  values: string[];
  disabled?: boolean;
  allowEmpty?:boolean;
  onChange: (values: string[]) => void;
  /**
   * Tên tiếng Hoa theo giá trị (loại sản phẩm — Roary 09/10/2026). Có thì mỗi dòng thêm một ô nhập; giá trị lưu vẫn là chữ
   * gốc, tên tiếng Hoa chỉ để hiện bên dưới trên các màn sản phẩm.
   */
  labels?: Record<string, string>;
  labelPlaceholder?: string;
  onLabelsChange?: (labels: Record<string, string>) => void;
}

export function OptionListEditor({
  title,
  description,
  placeholder,
  values,
  disabled = false,
  allowEmpty = false,
  onChange,
  labels,
  labelPlaceholder,
  onLabelsChange,
}: OptionListEditorProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const [draft, setDraft] = useState("");
  const trimmed = draft.trim();
  const duplicate = values.some(
    (value) => value.toLocaleLowerCase() === trimmed.toLocaleLowerCase(),
  );

  const add = () => {
    if (!trimmed || duplicate || disabled) return;
    onChange([...values, trimmed]);
    setDraft("");
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= values.length) return;
    const next = [...values];
    const currentValue = next[index];
    const targetValue = next[target];
    if (currentValue === undefined || targetValue === undefined) return;
    next[index] = targetValue;
    next[target] = currentValue;
    onChange(next);
  };

  return (
    <section aria-labelledby={titleId} className="rounded-md border border-line bg-paper/45">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3.5">
        <div className="min-w-0">
          <h4 id={titleId} className="text-sm font-semibold text-ink">{title}</h4>
          <p className="mt-0.5 max-w-2xl text-xs leading-relaxed text-ink-soft">{description}</p>
        </div>
        <span className="rounded-full border border-line bg-surface px-2 py-0.5 font-mono tabular text-2xs text-ink-faint">
          {t("settings.configuredCount", { count: values.length })}
        </span>
      </div>

      <div className="max-h-[min(52dvh,32rem)] divide-y divide-line overflow-y-auto overscroll-contain">
        {values.map((value, index) => (
          <div key={`${value}-${index}`} className="group flex min-h-11 items-center gap-3 px-3 sm:px-4">
            <span className="w-5 shrink-0 text-right font-mono tabular text-2xs text-ink-faint">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-ink">{value}</span>
            {labels && onLabelsChange ? (
              disabled ? (
                <span lang="zh-Hans" className="w-44 shrink-0 truncate text-sm text-ink-soft">{labels[value] || "—"}</span>
              ) : (
                <Input
                  lang="zh-Hans"
                  maxLength={120}
                  value={labels[value] ?? ""}
                  onChange={(event) => onLabelsChange({ ...labels, [value]: event.target.value })}
                  placeholder={labelPlaceholder}
                  aria-label={`${labelPlaceholder ?? ""}: ${value}`}
                  className="w-44 shrink-0"
                />
              )
            ) : null}
            {!disabled ? (
              <div className="flex shrink-0 items-center gap-0.5 opacity-70 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={`${t("settings.moveUp")}: ${value}`}
                  title={t("settings.moveUp")}
                >
                  <ArrowUp className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={index === values.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={`${t("settings.moveDown")}: ${value}`}
                  title={t("settings.moveDown")}
                >
                  <ArrowDown className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={!allowEmpty && values.length === 1}
                  onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
                  aria-label={`${t("settings.removeOption")}: ${value}`}
                  title={!allowEmpty && values.length === 1 ? t("settings.minimumOneOption") : t("settings.removeOption")}
                >
                  <Trash2 className="size-3.5 text-danger" />
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {!disabled ? (
        <div className="border-t border-line bg-surface p-3 sm:p-4">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              maxLength={120}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  add();
                }
              }}
              placeholder={placeholder}
              aria-label={placeholder}
              className="flex-1"
            />
            <Button
              variant="secondary"
              size="md"
              disabled={!trimmed || duplicate}
              onClick={add}
            >
              <Plus className="size-4" />
              {t("settings.addOption")}
            </Button>
          </div>
          {duplicate ? <p className="mt-1.5 text-xs text-warn">{t("settings.optionExists")}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
